import type { Car, CarOptionSet, GasStation } from "@/types/database";
import { resolveCarMakeLogo, type CarMakeLogoEntry } from "@/lib/car-make-logos";

export type FuelPriceKey =
  | "fuelPrice_87"
  | "fuelPrice_90"
  | "fuelPrice_Diesel"
  | "fuelPrice_ULSD";

export const FUEL_PRICE_LABELS: Record<FuelPriceKey, string> = {
  fuelPrice_87: "E-10 87",
  fuelPrice_90: "E-10 90",
  fuelPrice_Diesel: "Diesel",
  fuelPrice_ULSD: "ULSD",
};

export type FuelPricePoint = {
  price: number;
  station: GasStation;
  fuelKey: FuelPriceKey;
};

export type FuelPriceExtremes = {
  highest: FuelPricePoint | null;
  lowest: FuelPricePoint | null;
};

export function stationDisplayName(station: GasStation): string {
  const location = station.parish || station.address;
  return location ? `${station.name} · ${location}` : station.name;
}

export function stationsWithFuelPrice(
  stations: GasStation[],
  key: FuelPriceKey = "fuelPrice_87"
): GasStation[] {
  return stations.filter((s) => {
    const price = s[key];
    return price != null && price > 0;
  });
}

export function getFuelPriceExtremes(
  stations: GasStation[],
  key: FuelPriceKey = "fuelPrice_87"
): FuelPriceExtremes {
  const priced = stationsWithFuelPrice(stations, key);
  if (priced.length === 0) {
    return { highest: null, lowest: null };
  }

  let highest = priced[0];
  let lowest = priced[0];

  for (const station of priced) {
    const price = station[key]!;
    if (price > highest[key]!) highest = station;
    if (price < lowest[key]!) lowest = station;
  }

  return {
    highest: { price: highest[key]!, station: highest, fuelKey: key },
    lowest: { price: lowest[key]!, station: lowest, fuelKey: key },
  };
}

export function getRecentCars(cars: Car[], limit = 5): Car[] {
  return [...cars]
    .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
    .slice(0, limit);
}

export type ChartDatum = { name: string; value: number };

export function getCarsByMakeChart(cars: Car[], limit = 8): ChartDatum[] {
  const counts = new Map<string, number>();
  for (const car of cars) {
    const make = car.make?.trim() || "Unknown";
    counts.set(make, (counts.get(make) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, value]) => ({ name, value }));
}

export function getCarsByModelChart(cars: Car[], limit = 5): ChartDatum[] {
  return getPopularModelEntries(cars, limit).map(({ label, value }) => ({ name: label, value }));
}

export type PopularModelEntry = {
  make: string;
  model: string;
  label: string;
  value: number;
};

export function getPopularModelEntries(cars: Car[], limit = 5): PopularModelEntry[] {
  const counts = new Map<string, { make: string; model: string; count: number }>();

  for (const car of cars) {
    const make = car.make?.trim() || "";
    const model = car.model?.trim() || "Unknown";
    const key = make ? `${make}\0${model}` : model;
    const existing = counts.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(key, { make, model, count: 1 });
    }
  }

  return [...counts.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
    .map(({ make, model, count }) => ({
      make,
      model,
      label: make ? `${make} ${model}` : model,
      value: count,
    }));
}

export function getCarsByBodyChart(cars: Car[], limit = 6): ChartDatum[] {
  const counts = new Map<string, number>();
  for (const car of cars) {
    const body = car.body?.trim() || "Unspecified";
    counts.set(body, (counts.get(body) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, value]) => ({ name, value }));
}

export function getCarsExplorerInsights(cars: Car[]) {
  const models = getPopularModelEntries(cars, 1)[0] ?? null;
  const bodies = getCarsByBodyChart(cars, 1)[0] ?? null;
  const makes = new Set(cars.map((car) => car.make?.trim()).filter(Boolean)).size;
  const types = new Set(cars.map((car) => car.body?.trim()).filter(Boolean)).size;

  return {
    topModel: models,
    topBody: bodies,
    uniqueMakes: makes,
    uniqueTypes: types,
  };
}

export function getStationsByParishChart(stations: GasStation[], limit = 8): ChartDatum[] {
  const counts = new Map<string, number>();
  for (const station of stations) {
    const parish = station.parish?.trim() || "Unassigned";
    counts.set(parish, (counts.get(parish) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([name, value]) => ({ name, value }));
}

export function getFuelPriceHistogram(
  stations: GasStation[],
  bucketSize = 10
): ChartDatum[] {
  const priced = stationsWithFuelPrice(stations, "fuelPrice_87");
  if (priced.length === 0) return [];

  const prices = priced.map((s) => s.fuelPrice_87!);
  const min = Math.floor(Math.min(...prices) / bucketSize) * bucketSize;
  const max = Math.ceil(Math.max(...prices) / bucketSize) * bucketSize;

  const buckets: ChartDatum[] = [];
  for (let start = min; start < max; start += bucketSize) {
    const end = start + bucketSize;
    const count = prices.filter((p) => p >= start && (end >= max ? p <= end : p < end)).length;
    if (count > 0) {
      buckets.push({ name: `$${start}–${end}`, value: count });
    }
  }
  return buckets;
}

export function getAvgFuelPriceByBrand(
  stations: GasStation[],
  key: FuelPriceKey = "fuelPrice_87",
  limit = 8
): ChartDatum[] {
  const groups = new Map<string, number[]>();

  for (const station of stations) {
    const price = station[key];
    if (price == null || price <= 0) continue;
    const brand = station.name.trim() || "Unknown";
    const prices = groups.get(brand) ?? [];
    prices.push(price);
    groups.set(brand, prices);
  }

  return [...groups.entries()]
    .map(([name, prices]) => ({
      name,
      value: Math.round((prices.reduce((sum, price) => sum + price, 0) / prices.length) * 100) / 100,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);
}

export function getFuelTypeAverages(stations: GasStation[]): ChartDatum[] {
  const keys: FuelPriceKey[] = [
    "fuelPrice_87",
    "fuelPrice_90",
    "fuelPrice_Diesel",
    "fuelPrice_ULSD",
  ];

  return keys
    .map((key) => {
      const priced = stationsWithFuelPrice(stations, key);
      const average =
        priced.length > 0
          ? priced.reduce((sum, station) => sum + station[key]!, 0) / priced.length
          : 0;
      return {
        name: FUEL_PRICE_LABELS[key],
        value: Math.round(average * 100) / 100,
      };
    })
    .filter((entry) => entry.value > 0);
}

export type FuelNetworkCharts = {
  fuelHistogram: ChartDatum[];
  stationsByParish: ChartDatum[];
  avgPriceByBrand: ChartDatum[];
  fuelTypeAverages: ChartDatum[];
};

export function getFuelNetworkCharts(stations: GasStation[]): FuelNetworkCharts {
  return {
    fuelHistogram: getFuelPriceHistogram(stations),
    stationsByParish: getStationsByParishChart(stations),
    avgPriceByBrand: getAvgFuelPriceByBrand(stations),
    fuelTypeAverages: getFuelTypeAverages(stations),
  };
}

export type DashboardCharts = {
  carsByMake: ChartDatum[];
  stationsByParish: ChartDatum[];
  fuelHistogram: ChartDatum[];
};

export function getDashboardCharts(cars: Car[], stations: GasStation[]): DashboardCharts {
  return {
    carsByMake: getCarsByMakeChart(cars),
    stationsByParish: getStationsByParishChart(stations),
    fuelHistogram: getFuelPriceHistogram(stations),
  };
}

export function getNetworkOverview(cars: Car[], stations: GasStation[]) {
  const fuel87 = getFuelPriceExtremes(stations, "fuelPrice_87");
  const priced87 = stationsWithFuelPrice(stations, "fuelPrice_87");
  const prices = priced87.map((s) => s.fuelPrice_87!);
  const avgFuel87 =
    prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  const spread =
    fuel87.highest && fuel87.lowest
      ? fuel87.highest.price - fuel87.lowest.price
      : 0;

  return {
    carCount: cars.length,
    gasStationCount: stations.length,
    uniqueMakes: new Set(cars.map((c) => c.make).filter(Boolean)).size,
    uniqueOwners: new Set(cars.map((c) => c.userID).filter(Boolean)).size,
    stationsWithCoords: stations.filter(
      (s) => s.coordinatesLat != null && s.coordinatesLong != null
    ).length,
    stationsWithPricing: priced87.length,
    avgFuel87,
    spread,
    fuel87,
  };
}

export type NetworkOverview = ReturnType<typeof getNetworkOverview>;

export type PlatformOverview = {
  optionSetCount: number;
  uniqueMakes: number;
  totalModels: number;
  topMakes: { make: string; modelCount: number; logo: CarMakeLogoEntry | null }[];
};

export function getPlatformOverview(
  optionSets: Pick<CarOptionSet, "make" | "models">[],
  makeLogos: Map<string, CarMakeLogoEntry> = new Map()
): PlatformOverview {
  const topMakes = optionSets
    .map((row) => ({
      make: row.make,
      modelCount: row.models?.length ?? 0,
      logo: resolveCarMakeLogo(row.make, makeLogos),
    }))
    .sort((a, b) => b.modelCount - a.modelCount)
    .slice(0, 6);

  return {
    optionSetCount: optionSets.length,
    uniqueMakes: optionSets.length,
    totalModels: optionSets.reduce((sum, row) => sum + (row.models?.length ?? 0), 0),
    topMakes,
  };
}
